import { useEffect, useState } from "react";
import { api } from "../api/client";
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  List,
  ListItem,
  ListItemText,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';

export default function ArtifactsPanel() {
  const [version, setVersion] = useState("");
  const [apkUrl, setApkUrl] = useState("");
  const [flavor, setFlavor] = useState("all");
  const [releaseNotes, setReleaseNotes] = useState("");
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  async function refresh() {
    try {
      const artifacts = await api("/admin/artifacts");
      setItems(artifacts);
    } catch (err) {
      console.error("Failed to load artifacts:", err);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function add() {
    setError(null);
    setSuccess(null);
    try {
      await api("/admin/artifacts", {
        method: "POST",
        body: JSON.stringify({
          version,
          apkUrl,
          flavor,
          releaseNotes
        })
      });
      setVersion("");
      setApkUrl("");
      setFlavor("all");
      setReleaseNotes("");
      setSuccess(`APK artifact ${version} (${flavor}) added successfully!`);
      await refresh();
    } catch (err) {
      setError(err.message || "Failed to add artifact");
    }
  }

  async function deleteArtifact(artifactId, version) {
    if (!window.confirm(`Are you sure you want to delete artifact version ${version}?`)) {
      return;
    }

    setError(null);
    setSuccess(null);
    try {
      await api(`/admin/artifacts/${artifactId}`, {
        method: "DELETE"
      });
      setSuccess(`Artifact ${version} deleted successfully!`);
      await refresh();
    } catch (err) {
      setError(err.message || "Failed to delete artifact");
    }
  }

  const getFlavorColor = (f) => {
    switch(f) {
      case 'arrivals': return 'info';
      case 'departures': return 'warning';
      case 'all': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h6" mb={1}>APK Artifacts</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Register Android APK builds for deployment to fleet devices.
      </Typography>

      <Stack spacing={2} mb={2}>
        <Stack spacing={2} direction="row" alignItems="center">
          <TextField
            label="Version"
            value={version}
            onChange={e => setVersion(e.target.value)}
            placeholder="e.g., 0.1.4"
            sx={{ width: 150 }}
            size="small"
          />
          <FormControl size="small" sx={{ width: 150 }}>
            <InputLabel>Flavor</InputLabel>
            <Select
              value={flavor}
              label="Flavor"
              onChange={e => setFlavor(e.target.value)}
            >
              <MenuItem value="all">All Devices</MenuItem>
              <MenuItem value="arrivals">Arrivals Only</MenuItem>
              <MenuItem value="departures">Departures Only</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <TextField
          label="APK URL"
          value={apkUrl}
          onChange={e => setApkUrl(e.target.value)}
          placeholder="https://api.fids.eximenon.com/uploads/apks/app-departures-0.1.4.apk"
          fullWidth
          size="small"
          helperText="Direct HTTPS URL to the APK file"
        />

        <TextField
          label="Release Notes (Optional)"
          value={releaseNotes}
          onChange={e => setReleaseNotes(e.target.value)}
          placeholder="Bug fixes and performance improvements"
          fullWidth
          size="small"
          multiline
          rows={2}
        />

        <Button
          variant="contained"
          onClick={add}
          disabled={!version || !apkUrl}
          sx={{ alignSelf: 'flex-start' }}
        >
          Add APK Artifact
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Typography variant="subtitle2" mt={3} mb={1}>
        Registered Artifacts ({items.length})
      </Typography>
      <List>
        {items.map(a => (
          <ListItem
            key={a._id}
            divider
            secondaryAction={
              <IconButton
                edge="end"
                aria-label="delete"
                onClick={() => deleteArtifact(a._id, a.version)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            }
          >
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1">
                    Version {a.version}
                  </Typography>
                  <Chip
                    label={a.flavor}
                    size="small"
                    color={getFlavorColor(a.flavor)}
                  />
                </Stack>
              }
              secondary={
                <Stack spacing={0.5} mt={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    {a.apkUrl}
                  </Typography>
                  {a.releaseNotes && (
                    <Typography variant="caption" color="text.secondary">
                      📝 {a.releaseNotes}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.disabled">
                    Added {new Date(a.createdAt).toLocaleString()}
                  </Typography>
                </Stack>
              }
            />
          </ListItem>
        ))}
        {items.length === 0 && (
          <ListItem>
            <ListItemText
              secondary="No artifacts registered yet. Add your first APK above."
              sx={{ textAlign: 'center', py: 2 }}
            />
          </ListItem>
        )}
      </List>
    </Box>
  );
}
